import ArrowRight from "@/assets/icons/ArrowRight";
import AppButton from "@/components/shared/AppButton";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { Input, InputField } from "@/components/ui/input";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useTimedEvent } from "@/hooks/useTimedEvent";
import { verifyCodeSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { T, useT } from "@transifex/react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Pressable } from "react-native";
import type { z } from "zod";

type VerifyFormType = z.infer<typeof verifyCodeSchema>;

interface SignUpVerificationProps {
  errorMessage?: string;
  onVerifySubmit: (data: VerifyFormType) => void;
  onResendCode: () => void;
  isLoading: boolean;
}

const SignUpVerification: React.FC<SignUpVerificationProps> = ({ errorMessage, onVerifySubmit, onResendCode, isLoading }) => {
  const {
    control: verifyControl,
    handleSubmit: handleVerifySubmit,
    formState: { errors: verifyErrors },
    clearErrors: clearVerifyErrors,
    setError,
  } = useForm<VerifyFormType>({
    resolver: zodResolver(verifyCodeSchema),
  });
  const t = useT();

  const { enabled } = useTimedEvent(15000);

  useEffect(() => {
    if (errorMessage) {
      setError("code", { message: errorMessage });
    }
  }, [errorMessage, setError]);

  return (
    <VStack className="gap-[32px]">
      <Text className="text-[14px] font-[400] leading-[20px] text-center text-typography-900">
        <T _str="Please enter the verification code sent to your email." />
      </Text>

      <FormControl isInvalid={!!verifyErrors.code} key="verification-code-signup-input" className="gap-[10px]">
        <FormControlLabel>
          <FormControlLabelText className="flex flex-col text-typography-900 text-[14px] leading-[20px] font-semibold">
            <T _str="Verification Code" />
            {enabled && !isLoading && (
              <Pressable className="active:opacity-70" onPress={onResendCode}>
                <Text className="text-[14px] font-[400] leading-[20px] text-typography-900">
                  <T _str="Didn't receive the code?" />
                  {"  "}
                  <Text className="text-[14px] font-[700] leading-[20px] underline text-typography-900">
                    <T _str="Resend Code" />
                  </Text>
                </Text>
              </Pressable>
            )}
          </FormControlLabelText>
        </FormControlLabel>
        <Controller
          name="code"
          control={verifyControl}
          defaultValue=""
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px]  ${verifyErrors.code ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              isDisabled={isLoading}
            >
              <InputField
                placeholder={t("Code")}
                value={value || ""}
                onChangeText={(text: string) => onChange(text)}
                onBlur={onBlur}
                onFocus={() => clearVerifyErrors("code")}
                keyboardType="number-pad"
                maxLength={6}
                className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                autoFocus
              />
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{verifyErrors.code?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>
      <AppButton
        title={isLoading ? t("Verifying...") : t("Verify account")}
        onPress={handleVerifySubmit(onVerifySubmit)}
        loading={isLoading}
        icon={ArrowRight}
        iconPosition="after"
      />
    </VStack>
  );
};

export default SignUpVerification;

export const SignUpVerificationSkeleton = () => {
  return (
    <VStack className="gap-[32px]">
      <SkeletonText className="h-[20px] w-3/4 self-center" />
      <SkeletonText className="h-[14px] w-full self-center" />
      <VStack className="gap-[20px]">
        <SkeletonText className="h-[14px] w-2/5" />
        <SkeletonText className="h-[14px] w-3/4" />
        <Skeleton variant="rounded" className="h-[52px] w-full" />
      </VStack>
      <Skeleton variant="rounded" className="h-[52px] w-full" />
    </VStack>
  );
};
